import { useMemo, useState } from 'react';
import { ListGroup } from 'react-bootstrap';
import { BsChevronRight } from 'react-icons/bs';
import Fuse from 'fuse.js';
import Skeleton from 'react-loading-skeleton';
import { useMakes } from '../api';
import type { Make } from '../domain';
import { useConfiguratorState } from '../store';
import { ConfiguratorSearchInput, QueryStateBoundary } from '../common';
import { useConfiguratorTranslations } from '../translations';
import styles from '../configurator.module.scss';

const BRAND_COLLAPSED_LIMIT = 10;

const toTitleCase = (s: string) =>
	s.replace(
		/\S+/g,
		(w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()
	);

function useBrandSearch(makes: Make[], query: string) {
	const fuse = useMemo(
		() => new Fuse(makes, { keys: ['make'], threshold: 0.3 }),
		[makes]
	);

	const filteredMakes = useMemo(
		() => (query.trim() === '' ? makes : fuse.search(query).map((r) => r.item)),
		[query, fuse, makes]
	);

	return { filteredMakes };
}

function BrandListItem({ make }: { make: Make }) {
	const setMake = useConfiguratorState((s) => s.setMake);
	const tr = useConfiguratorTranslations();

	const fallbackUrl = `https://placehold.co/80x80?text=${encodeURIComponent(
		make.make
	)}`;

	return (
		<ListGroup.Item
			action
			onClick={() => setMake(make.code, make.make)}
			className="d-flex align-items-center gap-3"
			aria-label={tr.brandSelectAriaLabel(make.make)}
		>
			<img
				src={make.logoUrl}
				alt=""
				className={styles.brandLogo}
				loading="lazy"
				onError={(e) => {
					if (e.currentTarget.src !== fallbackUrl) {
						e.currentTarget.src = fallbackUrl;
					}
				}}
			/>
			<span className="flex-grow-1 fw-medium">{toTitleCase(make.make)}</span>
			<BsChevronRight className="text-muted" aria-hidden />
		</ListGroup.Item>
	);
}

function BrandList({ makes }: { makes: Make[] }) {
	const [expanded, setExpanded] = useState(false);
	const tr = useConfiguratorTranslations();

	if (makes.length === 0) {
		return <div className="text-muted">{tr.brandEmpty}</div>;
	}

	const visibleMakes = expanded ? makes : makes.slice(0, BRAND_COLLAPSED_LIMIT);

	return (
		<>
			<ListGroup>
				{visibleMakes.map((m) => (
					<BrandListItem key={m.make} make={m} />
				))}
			</ListGroup>
			{!expanded && makes.length > BRAND_COLLAPSED_LIMIT && (
				<ListGroup className="mt-3">
					<ListGroup.Item
						action
						onClick={() => setExpanded(true)}
						className="text-center text-muted fst-italic py-3"
					>
						{tr.brandShowAll(makes.length)}
					</ListGroup.Item>
				</ListGroup>
			)}
		</>
	);
}

function BrandSelectorContent({
	makes,
	search,
}: {
	makes: Make[];
	search: string;
}) {
	const { filteredMakes } = useBrandSearch(makes, search);
	return <BrandList makes={filteredMakes} />;
}

export function BrandSelector() {
	const query = useMakes();
	const tr = useConfiguratorTranslations();
	const [search, setSearch] = useState('');

	return (
		<section>
			<h1>{tr.brandHeading}</h1>
			<p className="text-muted">{tr.brandSubtitle}</p>
			<ConfiguratorSearchInput
				value={search}
				onChange={setSearch}
				placeholder={tr.brandSearchPlaceholder}
				className="mb-3"
			/>
			<QueryStateBoundary
				query={query}
				skeleton={<Skeleton count={5} height={48} />}
			>
				{(makes) => <BrandSelectorContent makes={makes} search={search} />}
			</QueryStateBoundary>
		</section>
	);
}
